<template>
	<div class="container_section" :class="slimHeader ? 'px-5 pb-5 pt-3' : 'p-5'">
		<EvolutionTableTitleBar
			:title="title"
			:show-title="!slimHeader"
			:selected-period="selectedPeriod"
			:period-options="periodOptions"
			:show-period-select="showPeriodSelect"
			:comparison-enabled="comparisonEnabled"
			:comparison-open="comparisonOpen"
			@change-period="$emit('change-period', $event)"
			@toggle-comparison="onToggleComparison" />

		<section>
			<!-- Vista de comparación: reemplaza la tabla en la misma card. -->
			<SalesComparisonChart v-if="comparisonOpen"
				v-bind="comparison"
				@select-item="$emit('comparison-select-item', $event)"
				@add-year="$emit('comparison-add-year', $event)"
				@remove-year="$emit('comparison-remove-year', $event)" />

			<div v-else-if="isLoading" class="loading_state py-6 has-text-centered">
				<p>Cargando datos...</p>
			</div>

			<div v-else-if="error" class="error_state py-6 has-text-centered">
				<p class="has-text-danger">{{ error }}</p>
				<button class="action_btn mt-3" @click="$emit('retry')">Reintentar</button>
			</div>

			<div v-else-if="monthHeaders.length === 0" class="empty_state py-6 has-text-centered">
				<p>{{ emptyMessage }}</p>
			</div>

			<div v-else class="table_container" ref="mainTable">
				<table class="custom_table" :style="tableStyle">
					<colgroup>
						<col style="width: 240px;" />
						<col v-if="showAccumulated" style="width: 160px;" />
						<col style="width: 160px;" v-for="header in monthHeaders" :key="header" />
					</colgroup>
					<thead>
						<tr>
							<th ref="firstStickyCell" scope="col" class="sticky-column header_label nobreak"></th>
							<th v-if="showAccumulated" scope="col"
								class="sticky-column sticky-column--acc header_label has-text-right nobreak">
								Acumulado
							</th>
							<th scope="col" class="has-text-right nobreak is-capitalized"
								v-for="header in monthHeaders" :key="header">
								{{ header }}
							</th>
						</tr>
					</thead>
					<tbody>
						<EvolutionTableDataRow
							v-for="row in visibleRows" :key="`${row.level}-${row.parent || ''}-${row.label}`"
							:row="row"
							:month-headers="monthHeaders"
							:expanded-l2-rows="expandedL2Rows"
							:show-accumulated="showAccumulated"
							:cells-clickable="clickableCells"
							:percent-mode="percentMode"
							:sales-values="salesValues"
							:cell-tooltips="cellTooltips"
							:filter-period="filterPeriod"
							@toggle-l2="$emit('toggle-l2', $event)"
							@cell-click="$emit('cell-click', $event)" />

						<tr class="theme_row_highlight"
							:style="{ '--row-sticky-bottom': totalRowStickyBottom }">
							<th scope="row" class="sticky-column nobreak theme_row_highlight">
								{{ totalRow.label }}
							</th>
							<td v-if="showAccumulated"
								class="sticky-column sticky-column--acc has-text-right nobreak acc_cell theme_row_highlight">
								<span class="acc_amount">{{ parseToMoney(totalRow.accumulated) }}</span>
								<span class="acc_pct" :class="pctClass(totalRow.pct)">
									{{ formatPct(totalRow.pct) }}
								</span>
							</td>
							<td v-for="(val, idx) in totalRow.values" :key="'total-' + idx"
								class="has-text-right nobreak theme_row_highlight"
								:class="{ clickable_cell: clickableCells }"
								@click="onCellClick(monthHeaders[idx], null)">
								<span v-if="cellTooltips" class="cell_value"
									v-tooltip.top="cellVariationTooltip(totalRow, idx, filterPeriod)">
									{{ parseToMoney(val) }}
								</span>
								<template v-else>{{ parseToMoney(val) }}</template>
							</td>
						</tr>

						<tr v-for="(row, ridx) in level2RatioRows" :key="'l2r-' + row.label"
							class="theme_row_secondary"
							:style="{ '--row-sticky-bottom': l2RatioStickyBottom(ridx) }">
							<th scope="row" class="sticky-column nobreak theme_row_secondary">
								{{ row.label }}
							</th>
							<td v-if="showAccumulated"
								class="sticky-column sticky-column--acc has-text-right nobreak theme_row_secondary acc_cell--ratio">
								—
							</td>
							<td v-for="(val, idx) in row.values" :key="'l2r-' + ridx + '-' + idx"
								class="has-text-right nobreak theme_row_secondary">
								{{ formatPercent(val) }}
							</td>
						</tr>

						<tr class="theme_row_secondary">
							<th scope="row" class="sticky-column nobreak theme_row_secondary">
								{{ ratioRow.label }}
							</th>
							<td v-if="showAccumulated"
								class="sticky-column sticky-column--acc has-text-right nobreak theme_row_secondary acc_cell--ratio">
								—
							</td>
							<td v-for="(val, idx) in ratioRow.values" :key="'eff-' + idx"
								class="has-text-right nobreak theme_row_secondary">
								{{ formatPercent(val) }}
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</section>
	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";
import { formatPercent } from "@/views/financialReports/utils/periodFormatting";
import { formatVariation } from "@/utils/percentage";
import { cellVariationTooltip } from "@/views/financialReports/utils/cellTooltip";
import EvolutionTableTitleBar from "./EvolutionTableTitleBar.vue";
import EvolutionTableDataRow from "./EvolutionTableDataRow.vue";
import SalesComparisonChart from "./charts/SalesComparisonChart.vue";

// Alto fijo de th/td (ver CSS `height: 44px`). Sticky-bottom de las filas
// total/ratio offsetea por múltiplos de este número.
const ROW_HEIGHT_PX = 44;

export default {
	name: "ExpensesEvolutionTable",

	components: { EvolutionTableTitleBar, EvolutionTableDataRow, SalesComparisonChart },

	props: {
		monthHeaders: { type: Array, required: true },
		visibleRows: { type: Array, required: true },
		totalRow: { type: Object, required: true },
		ratioRow: { type: Object, required: true },
		// Filas opcionales de ratio por level_2 (Costos). Se renderizan entre
		// totalRow y ratioRow, todas sticky-bottom apiladas. Vacío en Gastos.
		level2RatioRows: { type: Array, default: () => [] },
		expandedL2Rows: { type: Array, default: () => [] },
		isLoading: { type: Boolean, default: false },
		error: { type: String, default: "" },
		selectedPeriod: { type: String, required: true },
		periodOptions: { type: Array, required: true },
		// Permite reusar la tabla en Costos sin duplicar markup. Default mantiene
		// el comportamiento existente para no romper GastosTab.
		title: { type: String, default: "Evolución de Gastos & Eficiencia" },
		emptyMessage: { type: String, default: "No hay datos de gastos para el período seleccionado." },
		// Opt-in: activa la 2da columna sticky "Acumulado" (monto + pct vs
		// ventana previa). Cada fila debe traer `accumulated` y `pct` en su
		// shape. Las filas de ratio (totalRow excepto, ratioRow, level2RatioRows)
		// muestran "—" porque el ratio acumulado no es agregable trivialmente.
		showAccumulated: { type: Boolean, default: false },
		// Granularidad activa (month/quarter/year/ytd). Cuando es year/ytd el
		// rango ya es implícito y ocultamos el selector de períodos — mismo
		// criterio que FinancialResultsTable / SalesEvolutionTable.
		filterPeriod: { type: String, default: "month" },
		// Opt-in del drill-down: hace clickeables las celdas (datos + total) en
		// TODAS las granularidades. El modal de análisis es mensual; el tab mapea
		// la columna clickeada al último mes del período (`periodLastMonth`).
		// Costos/Gastos lo activan; Activos/NoOper lo dejan en default false
		// (sin afordancia ni cell-click).
		clickableCells: { type: Boolean, default: false },
		// Opt-out del selector de período in-table. Default true (Costos/Activos/
		// NoOper lo usan). Gastos lo apaga: su rango se maneja desde el calendar
		// del header (modo range), igual que Ventas.
		periodSelectEnabled: { type: Boolean, default: true },
		// % de ventas: cuando true, las filas de datos (L2/L3) muestran sus
		// celdas mensuales como share sobre ventas (gasto / ventas de la
		// columna). La fila Total y las de ratio se mantienen. Lo gobierna el
		// mismo toggle que el chart (vive en GastosTab).
		percentMode: { type: Boolean, default: false },
		// Ventas netas por etiqueta de período (denominador del share). Mapa
		// { "Ene 2025": 1234567, ... }; se alinea a monthHeaders en `salesValues`.
		salesByPeriod: { type: Object, default: () => ({}) },
		// Opt-in: tooltip per-cell con MoM/QoQ + YoY (como Ventas). Lo activa
		// Gastos; requiere que las filas traigan `prevYearValues[]`. Default
		// false → Costos/Activos/NoOper no cambian.
		cellTooltips: { type: Boolean, default: false },
		// Opt-in: habilita el toggle "Comparación" que reemplaza la tabla por el
		// gráfico año-contra-año. Default false → el resto de los tabs no lo ven.
		comparisonEnabled: { type: Boolean, default: false },
		// Opt-in: header delgado sin título (sólo las acciones, alineadas a la
		// derecha) + menos padding-top de la card. Lo usa Gastos, que ya tiene un
		// título general arriba; replica el header de SalesEvolutionTable.
		slimHeader: { type: Boolean, default: false },
		// View-model que se pasa tal cual a SalesComparisonChart vía v-bind
		// (series, months, isLoading, error, years, availableYears, subItems,
		// selectedItem, period). Bundle único para no inflar la firma.
		comparison: { type: Object, default: () => ({}) },
	},

	data() {
		// Fallback inicial = <col width> del primer col (240px en este tabla).
		// Se sobrescribe en mounted() cuando showAccumulated está activo.
		// `resizeObserver` arranca null y se setea en attachResizeObserver;
		// vivir en data() (en vez de `this._xxx`) es forward-compat con Vue 3
		// — el Proxy intercepta toda asignación a `this`, así que el truco del
		// prefijo "_" deja de aplicar.
		return { firstColWidth: 240, resizeObserver: null, comparisonOpen: false };
	},

	computed: {
		tableStyle() {
			if (!this.showAccumulated) return null;
			return { "--sticky-col-1-width": this.firstColWidth + "px" };
		},
		// Total se apila por encima de las N filas L2-ratio + la ratioRow base.
		totalRowStickyBottom() {
			return ((this.level2RatioRows.length + 1) * ROW_HEIGHT_PX) + "px";
		},
		showPeriodSelect() {
			return this.periodSelectEnabled && !["ytd", "year"].includes(this.filterPeriod);
		},
		// Ventas por columna alineadas a monthHeaders — denominador del % de
		// ventas que aplica cada fila de datos.
		salesValues() {
			return this.monthHeaders.map((h) => this.salesByPeriod[h] ?? 0);
		},
	},

	watch: {
		monthHeaders() {
			this.scrollToRight();
			if (!this.showAccumulated) return;
			this.$nextTick(() => {
				this.measureFirstCol();
				this.attachResizeObserver();
			});
		},
		isLoading(val) {
			if (val || !this.showAccumulated) return;
			this.$nextTick(() => {
				this.measureFirstCol();
				this.attachResizeObserver();
			});
		},
		// Si el caller deshabilita la comparación con el panel abierto, lo
		// cerramos para no dejar un gráfico colgado sin su toggle.
		comparisonEnabled(enabled) {
			if (!enabled && this.comparisonOpen) {
				this.comparisonOpen = false;
				this.$emit("toggle-comparison", false);
			}
		},
	},
	mounted() {
		if (!this.showAccumulated) return;
		this.$nextTick(() => {
			this.measureFirstCol();
			this.attachResizeObserver();
		});
	},

	beforeDestroy() {
		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	},

	methods: {
		parseToMoney,
		formatPercent,
		cellVariationTooltip,
		// Alterna tabla ↔ comparación. Emite `toggle-comparison` con el nuevo
		// estado para que el caller dispare el fetch lazy del gráfico.
		onToggleComparison(open) {
			this.comparisonOpen = open;
			this.$emit("toggle-comparison", open);
		},
		// Filas L2-ratio: la primera (idx 0) es la más alejada del fondo;
		// la última (idx L-1) queda inmediatamente sobre ratioRow.
		l2RatioStickyBottom(idx) {
			return ((this.level2RatioRows.length - idx) * ROW_HEIGHT_PX) + "px";
		},
		scrollToRight() {
			this.$nextTick(() => {
				const table = this.$refs.mainTable;
				if (table) table.scrollLeft = table.scrollWidth;
			});
		},
		// Mide el ancho real de la primera columna y lo expone como CSS var en
		// la <table>; .sticky-column--acc usa esa var como offset `left`. Ver
		// SalesEvolutionTable para el patrón base.
		measureFirstCol() {
			const cell = this.$refs.firstStickyCell;
			if (cell) this.firstColWidth = cell.offsetWidth;
		},
		attachResizeObserver() {
			if (typeof ResizeObserver === "undefined") return;
			const cell = this.$refs.firstStickyCell;
			if (!cell) return;
			if (this.resizeObserver) this.resizeObserver.disconnect();
			this.resizeObserver = new ResizeObserver(() => this.measureFirstCol());
			this.resizeObserver.observe(cell);
		},
		// pct viene como ratio (0.123 = +12,3%), null cuando no hay comparable.
		// Delegamos en el formateador canónico — withArrow + withSign reproduce
		// "▲ +12,3%" / "▼ -12,3%" / "0,0%" / "—" del formato original.
		formatPct(pct) {
			return formatVariation(pct, { withArrow: true, withSign: true });
		},
		pctClass(pct) {
			if (pct == null) return "acc_pct--neutral";
			if (pct > 0) return "acc_pct--positive";
			if (pct < 0) return "acc_pct--negative";
			return "acc_pct--neutral";
		},
		// Payload `cell-click` uniforme con SalesEvolutionTable: lleva la row
		// entera (account/level/parent) para que el padre decida el drill-down.
		// La fila total emite row=null → account=null ("mes completo").
		onCellClick(monthLabel, row) {
			if (!this.clickableCells) return;
			this.$emit("cell-click", {
				monthLabel,
				account: row ? (row.account != null ? row.account : row.label) : null,
				accountGroup: row && row.accountGroup != null ? row.accountGroup : null,
				level: row && row.level != null ? row.level : null,
				parent: row && row.parent != null ? row.parent : null,
			});
		},
	},

};
</script>

<style scoped lang="scss">
.container_section {
	background: var(--report-bg);
	border-radius: 16px;
	border: 1px solid var(--report-border);
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

// "Reintentar" del error state reusa el mismo botón que el title bar.
// Style duplicado a propósito porque cada SFC scoped sólo alcanza su propio
// markup — el subcomponente y este parent comparten clase pero no estilos.
.action_btn {
	background: var(--report-bg);
	border: 1px solid var(--report-border);
	border-radius: 8px;
	padding: 6px 12px;
	font-size: 14px;
	font-weight: 500;
	display: flex;
	align-items: center;
	cursor: pointer;

	&:hover { background: var(--report-surface); }
}

.table_container {
	// Scroll en ambos ejes: x para periodos, y para listas largas.
	overflow: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
	// Alto máx ≈ thead (44) + 11 filas datos + totales (44) + ratio (44) = 14×44 = 616px.
	// max-height (no height) → si hay menos filas, la tabla se ajusta.
	max-height: calc(44px * 14);
	// Aísla el subárbol — repaints/reflows no se propagan.
	contain: layout paint;
}

.custom_table {
	min-width: 100%;
	border-collapse: separate;
	border-spacing: 0;

	th, td {
		// height fijo para que el cálculo de `bottom` de la fila superior
		// del bloque sticky-bottom (ratio + totales) coincida con el alto
		// real de la fila de abajo.
		height: 44px;
		padding: 12px 16px;
		border-bottom: 1px solid var(--report-divider-soft);
		font-size: 13px;
	}

	thead th {
		// Header sólido: banda con el primary del tab (ámbar/slate/emerald/…)
		// y texto blanco. Vía tokens, cada tab toma su color.
		background: var(--report-primary);
		color: var(--report-bg);
		font-weight: 700;
		text-align: right;
		// Sticky-top: encabezado siempre visible al scrollear verticalmente.
		position: sticky;
		top: 0;
		z-index: 3;
	}

	// Corner sticky + columna "Acumulado" (ambas con clase header_label):
	// primary más oscuro que la banda de meses para anclar el marco sticky.
	.header_label {
		background: var(--report-primary-hover);
	}

	.sticky-column {
		position: sticky;
		left: 0;
		z-index: 2;
		border-right: 1px solid var(--report-border);
	}

	// Segunda columna sticky (Acumulado): el offset `left` se calcula en
	// runtime midiendo el ancho real de la primera columna con ResizeObserver
	// y exponiéndolo como --sticky-col-1-width en el style inline de la
	// <table>. El fallback 240px coincide con el hint del <col> para el
	// primer paint. Solo aplica cuando showAccumulated=true.
	.sticky-column--acc {
		left: var(--sticky-col-1-width, 240px);
		box-sizing: border-box;
	}

	// Celda apilada del acumulado: monto arriba (énfasis), pct abajo con
	// flecha y color según signo. Espejo del patrón de SalesEvolutionTable.
	.acc_cell {
		.acc_amount {
			display: block;
			color: var(--report-text);
			font-size: 13px;
			font-weight: 500;
			line-height: 18px;
		}

		.acc_pct {
			display: block;
			font-size: 12px;
			font-weight: 500;
			line-height: 16px;
			margin-top: 2px;
		}

		.acc_pct--positive { color: var(--report-positive); }
		.acc_pct--negative { color: var(--report-negative); }
		.acc_pct--neutral  { color: var(--report-text-muted); }
	}

	// Filas de ratio (% Costos/Ventas, ratios L2): el acumulado de un ratio
	// no es trivialmente agregable, así que mostramos "—" muted en lugar de
	// un valor potencialmente engañoso.
	.acc_cell--ratio {
		color: var(--report-text-muted);
	}

	.theme_row_white { background: var(--report-bg); }

	// Ancla del tooltip per-cell: la caja envuelve sólo el número para que el
	// tooltip aparezca sobre el texto y no centrado en un <td> ancho.
	.cell_value {
		display: inline-block;
	}

	// Afordancia de drill-down (Costos): solo celdas mensuales clickeables.
	// El :hover sobre <td> mantiene el realce en la celda apuntada, no en
	// toda la fila. Espejo de SalesEvolutionTable.
	.clickable_cell {
		cursor: pointer;
		transition: background-color 0.12s ease;

		&:hover {
			background-color: var(--report-accent-bg);
		}
	}

	.theme_row_highlight {
		// Cada data-theme (gastos/costos/...) puede definir su propio highlight.
		// El default amber del sistema (en :root de _report_themes.scss) sirve
		// para los temas que no overridan — antes el fallback estaba inline.
		background: var(--report-highlight-bg);
		font-weight: 700;

		th, td {
			border-bottom: 1px solid var(--report-highlight-border);
			position: sticky;
			// Overridable por inline-style del <tr> en Costos, donde el total
			// debe apilarse encima de N filas de ratio L2.
			bottom: var(--row-sticky-bottom, 44px);
			z-index: 3;
		}
	}

	.theme_row_secondary {
		background: var(--report-surface);
		color: var(--report-text-muted);
		font-weight: 600;

		th, td {
			// Sticky-bottom: anclado al borde inferior del scroll-container.
			// Overridable por inline-style del <tr> para apilar varias filas
			// secondary (ratio total + ratios L2 en Costos).
			position: sticky;
			bottom: var(--row-sticky-bottom, 0px);
			z-index: 3;
		}
	}

	// Esquinas sticky-en-dos-ejes (top-left del thead y bottom-left de las
	// dos filas pinneadas abajo): suben a z-index 4 para pintar por encima
	// de los stickies de un solo eje. Incluye la columna "Acumulado"
	// (sticky-column--acc) en todas las filas pinneadas verticalmente.
	thead .sticky-column,
	thead .sticky-column--acc,
	.theme_row_highlight .sticky-column,
	.theme_row_highlight .sticky-column--acc,
	.theme_row_secondary .sticky-column,
	.theme_row_secondary .sticky-column--acc {
		z-index: 4;
	}

	.row-level-2 {
		font-weight: 700;
		background: var(--report-row-emphasis);
	}

	.row-level-3 {
		color: var(--report-text-muted-strong);

		th, td {
			font-size: 12.5px !important;
			color: var(--report-text-muted) !important;
		}
	}

	.pl-5 { padding-left: 32px !important; }

	.expand-icon {
		width: 14px;
		height: 14px;
		transition: transform 0.3s ease;
		color: var(--report-text-muted-soft);
	}

	.rotate-icon { transform: rotate(180deg); }
}
</style>
